import React, { memo, useMemo, useCallback } from 'react'
import { Text, Document, Page, View, Image, Font, } from '@react-pdf/renderer'
import PropTypes from 'prop-types'
import { toWords } from 'number-to-words'
import moment from 'moment'

import Icon from '@assets/images/icon.png'
import { dateFormat, dateOfWeeks } from '@utils/date'
import { cheatMealsData, dietData, watersText } from '@config/data'
import FontHelveticaBold from '@assets/fonts/HelveticaLTStd-Bold.otf'
import FontHelveticaRoman from '@assets/fonts/HelveticaLTStd-Roman.otf'
import { Circle } from '@components/atoms/circle'

Font.register({
  family: 'Helvetica-Bold',
  src: FontHelveticaBold
});

Font.register({
  family: 'Helvetica',
  src: FontHelveticaRoman
})
Font.registerHyphenationCallback(word => [word]);


const borderStyle = '1px solid black'

export const Output = memo(({ data }) => {
  const {
    firstName,
    lastName,
    weeks,
    startDate,
    startWeekOn,
    diets,
    cheatMeals,
    drinkWater,
    strengthTrainingMin,
    strengthTrainingMax,
    cardioMin,
    cardioMax,
    additionalGoals,
    weeksGoals
  } = data ?? {}

  const fullName = `${firstName} ${lastName}`;

  const introduction = useMemo(() => {
    const lastDate = () => {
      const date = dateOfWeeks(startWeekOn, weeks)[weeks - 1];
      return dateFormat(moment(date).subtract(1, 'd'));
    }

    const commits = () => {
      const entries = Object.entries(diets).filter(([, value]) => value)
      const filteredEntries = Object.fromEntries(entries)

      const objKeys = Object.keys(filteredEntries)
      const values = objKeys.map((x) => dietData[x])
      return `${values.slice(0, values.length - 1).join(', ')} and ${objKeys.slice(values.length - 1).map((x) => dietData[x])}`
    }

    const strengthTrainingText = () => {
      if (strengthTrainingMin === strengthTrainingMax)
        return `${toWords(strengthTrainingMin)} days per week`

      if (strengthTrainingMin === 7 && strengthTrainingMax === 7)
        return 'everyday'

      return `${toWords(strengthTrainingMin)} to ${toWords(strengthTrainingMax)} days per week`
    }

    const cardioText = () => {
      if (cardioMin === cardioMax)
        return `${toWords(cardioMin)} days per week`

      if (cardioMin === 7 && cardioMax === 7)
        return 'everyday'

      return `${toWords(cardioMin)} to ${toWords(cardioMax)} days per week`
    }

    const goalsText = () => {

      if (additionalGoals.length) {
        const goalsArr = additionalGoals.map(item => `${item.description} ${toWords(item.minDays)} to ${toWords(item.maxDays)} days per week`)

        const res = `In addition to my fitness goals, I will also commit to ${goalsArr.slice(0, goalsArr.length - 1).join(', ')} and ${goalsArr.slice(-1)}.`

        return res;
      }

      return ''
    }

    return (
      <View style={{ margin: 'auto' }}>
        <View
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <Text style={{
            fontSize: 22,
            fontWeight: 'bold',
            fontFamily: 'Helvetica-Bold'
          }}>
            {`${firstName} ${lastName}`}&#39;s
          </Text>
          <Text
            style={{
              fontSize: 22,
              fontWeight: 'bold',
              fontFamily: 'Helvetica-Bold'
            }}>
            Checkmark Fitness Challenge Pact
          </Text>
        </View>
        <View style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontSize: 14,
          textAlign: 'justify',
          lineHeight: 1.6,
          marginHorizontal: 56,
          padding: 36,
          border: '1px solid black',
        }}>
          <Text>
            I, {fullName}, pledge to take the Checkmark Fitness Challenge. I will commit {toWords(weeks)} weeks to building a stronger body and mind from {dateFormat(startDate)} , until {lastDate}. I will commit to {commits()} {cheatMealsData[cheatMeals]}. {drinkWater !== 1 && `I will drink at least ${watersText[drinkWater]} of water every day. `}{!!strengthTrainingMax && `I will push myself ${!!strengthTrainingMin && !!strengthTrainingMax && `with strength training ${strengthTrainingText()}`}`} {!!strengthTrainingMax && !!cardioMax && 'and '}{!!cardioMin && !!cardioMax && `with cardio ${cardioText()}. `}I will take progress photos every four weeks to document my progress. {goalsText()} These goals may be difficult, but with the support of Checkmark Fitness community, I will succeed!
          </Text>
          <View style={{
            borderTop: '1px solid black',
            marginTop: 78,
            textAlign: 'center',
            color: 'gray'
          }}>
            <Text style={{ marginTop: 12 }}>{fullName}</Text>
          </View>
          <View style={{ position: 'absolute', right: 0, left: 0, top: 0, bottom: 0, backgroundColor: '#e2e2e2', zIndex: -1, padding: 12 }} />
        </View>
      </View>
    )
  }, [additionalGoals, cardioMax, cardioMin, cheatMeals, diets, drinkWater, firstName, fullName, lastName, startDate, startWeekOn, strengthTrainingMax, strengthTrainingMin, weeks])

  const headerColumn = useMemo(() => {
    const additionalMin = additionalGoals.reduce((items, item) => item.minDays + items, 0);
    const gallonOfWaterInWeek = 7
    const dietInWeek = 7
    const totalCirclesInWeek = strengthTrainingMin + cardioMin + gallonOfWaterInWeek + dietInWeek + additionalMin

    return (
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <View style={{ width: 100 }}>
          <View style={{ height: 120 }} >
            <Text style={{ fontSize: 8 }}>/{totalCirclesInWeek}</Text>
          </View>
        </View>
        <View style={{ flex: 1, marginLeft: 10 }}>
          <View style={{ backgroundColor: '#1075bc', height: 50, border: borderStyle, justifyContent: 'center', color: 'white', textAlign: 'center', fontSize: 14 }} ><Text>{fullName}&#39;s Body & Mind Goals</Text></View>
          <View style={{ display: 'flex', flexDirection: 'row', borderBottom: borderStyle, borderRight: borderStyle, fontSize: 10, textAlign: 'center' }}>
            <View style={{ paddingHorizontal: 2, borderLeft: borderStyle, height: 60, flex: 1, display: 'flex', justifyContent: 'center', }} ><Text>Strength Training</Text></View>
            <View style={{ paddingHorizontal: 2, borderLeft: borderStyle, height: 60, flex: 1, display: 'flex', justifyContent: 'center', }} ><Text>Cardio</Text></View>
            <View style={{ paddingHorizontal: 2, borderLeft: borderStyle, height: 60, flex: 1, display: 'flex', justifyContent: 'center', }} ><Text>1 Gallon of Water</Text></View>
            <View style={{ paddingHorizontal: 2, borderLeft: borderStyle, height: 60, flex: 1, display: 'flex', justifyContent: 'center', }} ><Text>Diet</Text></View>
            {additionalGoals.map(x => (
              <View key={x.description} style={{ paddingHorizontal: 2, borderLeft: borderStyle, height: 60, flex: 1, display: 'flex', justifyContent: 'center', }} ><Text>{x.description}</Text></View>
            ))}
          </View>
        </View>
      </View>
    )
  }, [additionalGoals, cardioMin, fullName, strengthTrainingMin]);

  const dateColumn = useCallback(({ name, items }) => (
    <View style={{ width: 100, border: borderStyle, }}>
      <View style={{ display: 'flex', flexDirection: 'row' }}>
        <View style={{ height: 100, width: 20, backgroundColor: '#1075bc', borderRight: borderStyle, borderBottom: borderStyle }} >
          <View style={{ transform: 'rotate(-90deg)', width: 40, position: 'absolute', left: -10, top: 42 }}>
            <Text style={{ fontSize: 9, color: 'white' }}>{name}</Text>
          </View>
        </View>
        <View style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map(date => (
            <View style={{ borderBottom: borderStyle, flex: 1, width: 79, alignItems: 'center', justifyContent: 'center' }} >
              <Text style={{ fontSize: 8, textAlign: 'center', }}>{date}</Text>
            </View>
          ))}
        </View>
      </View>
      <View style={{ height: 40, justifyContent: 'center', alignItems: 'flex-end', paddingHorizontal: 6 }} >
        <Text>%</Text>
      </View>
    </View>
  ), []);

  const contentColumn = useMemo(() => {
    const strengthIterationMin = Math.floor(strengthTrainingMax / 2)
    const strengthIterationMax = Math.floor(strengthTrainingMax - strengthIterationMin)

    const cardioIterationMin = Math.floor(cardioMax / 2)
    const cardIterationMax = Math.floor(cardioMax - cardioIterationMin)

    const iterationMin = (numMax) => Math.floor(numMax / 2)
    const iterationMax = (numMax) => Math.floor(numMax - iterationMin(numMax))
    return (
      <View style={{ display: 'flex', flexDirection: 'row', flex: 1, border: borderStyle }}>
        {/* Strengh Training */}
        <View style={{ display: 'flex', flexDirection: 'column', flex: 1, borderRight: borderStyle }}>
          <View style={{ borderBottom: borderStyle, flex: 1 }} />
          <View style={{ borderBottom: borderStyle, flex: 1 }} />
          <View style={{ borderBottom: borderStyle, flex: 1 }} />
          <View style={{ borderBottom: borderStyle, flex: 1 }} />
          <View style={{ borderBottom: borderStyle, flex: 1 }} />
          <View style={{ borderBottom: borderStyle, flex: 1 }} />
          <View style={{ borderBottom: borderStyle, flex: 1 }} />
          <View style={{ height: 40, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }} >
            <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1, flexDirection: 'column' }}>
              <View style={{ flexDirection: 'row' }}>
                {[...Array(strengthIterationMin).keys()].map(x => (
                  <Circle type='min' key={x} />
                ))}
              </View>
              <View style={{ flexDirection: 'row' }}>
                {[...Array(strengthIterationMax).keys()].map(x => {
                  if (x + 1 >= strengthTrainingMin) return (
                    <Circle type='max' key={x} />
                  )
                  return <Circle type='min' key={x} />
                }
                )}
              </View>
            </View>
          </View>
        </View>
        {/* Cardio */}
        <View style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {[...Array(7).keys()].map(x => (
            <View key={x} style={{ flex: 1, justifyContent: 'center', alignItems: 'center', borderBottom: borderStyle }} >
              <View style={{ border: '1px solid black', padding: 4 }} />
            </View>
          ))}
          <View style={{ height: 40, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }} >
            <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1, flexDirection: 'column' }}>
              <View style={{ flexDirection: 'row' }}>
                {[...Array(cardioIterationMin).keys()].map(x => (
                  <Circle type='min' key={x} />
                ))}
              </View>
              <View style={{ flexDirection: 'row' }}>
                {[...Array(cardIterationMax).keys()].map(x => {
                  if (x + 1 >= cardioMin) return (
                    <Circle type='max' key={x} />
                  )
                  return <Circle type='min' key={x} />
                }
                )}
              </View>
            </View>
          </View>
        </View>
        {/* 1 Gallon of Water */}
        <View style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {[...Array(7).keys()].map(x => (
            <View key={x} style={{ borderBottom: borderStyle, borderLeft: borderStyle, flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }} >
              {[...Array(4).keys()].map(x => (
                <View key={x} style={{ border: '1px solid black', padding: 3, marginHorizontal: 2 }} />
              ))}
            </View>
          ))}
          <View style={{ height: 40, borderLeft: borderStyle, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }} >
            <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1, flexDirection: 'column' }}>
              <View style={{ flexDirection: 'row' }}>
                <Circle type='min' />
                <Circle type='min' />
                <Circle type='min' />
              </View>
              <View style={{ flexDirection: 'row' }}>
                <Circle type='min' />
                <Circle type='min' />
                <Circle type='min' />
                <Circle type='min' />
              </View>
            </View>
          </View>
        </View>
        {/* Diet */}
        <View style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {[...Array(7).keys()].map(x => (
            <View key={x} style={{ borderBottom: borderStyle, borderLeft: borderStyle, flex: 1, justifyContent: 'center', alignItems: 'center' }} >
              <View style={{ border: '1px solid black', padding: 4 }} />
            </View>
          ))}
          <View style={{ height: 40, borderLeft: borderStyle, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }} >
            <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1, flexDirection: 'column' }}>
              <View style={{ flexDirection: 'row' }}>
                <Circle type='min' />
                <Circle type='min' />
                <Circle type='min' />
              </View>
              <View style={{ flexDirection: 'row' }}>
                <Circle type='min' />
                <Circle type='min' />
                <Circle type='min' />
                <Circle type='min' />
              </View>
            </View>
          </View>
        </View>
        {/* Additional */}
        {additionalGoals.map(z => (
          <View key={z.description} style={{ display: 'flex', flexDirection: 'column', flex: 1, borderLeft: borderStyle, }}>
            {[...Array(7).keys()].map(x => (
              <View key={x} style={{ borderBottom: borderStyle, flex: 1, justifyContent: 'center', alignItems: 'center' }} >
                <View style={{ border: '1px solid black', padding: 4 }} />
              </View>
            ))}
            <View style={{ height: 40, flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }} >
              <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1, flexDirection: 'column' }}>
                <View style={{ flexDirection: 'row' }}>
                  {[...Array(iterationMin(z.maxDays)).keys()].map(x => (
                    <Circle type='min' key={x} />
                  ))}
                </View>
                <View style={{ flexDirection: 'row' }}>
                  {[...Array(iterationMax(z.maxDays)).keys()].map(x => {
                    if (x + 1 >= z.minDays) return (
                      <Circle type='max' key={x} />
                    )
                    return <Circle type='min' key={x} />
                  }
                  )}
                </View>
              </View>
            </View>
          </View>
        ))}
      </View>
    )
  }, [additionalGoals, cardioMax, cardioMin, strengthTrainingMax, strengthTrainingMin]);

  return (
    <Document>
      <Page size="A4" style={{ position: 'relative', padding: 35 }} wrap>
        {introduction}
        {weeksGoals.map(parent => (
          <>
            <View break />
            {headerColumn}
            {parent.map(y => (
              <View style={{ display: 'flex', flexDirection: 'row', marginBottom: 8 }}>
                {dateColumn({ ...y })}
                <View style={{ flex: 1, marginLeft: 10, flexDirection: 'column' }}>
                  {contentColumn}
                </View>
              </View>
            ))}
          </>
        ))}
        <View style={{ position: 'absolute', bottom: 40, left: 0, right: 0, alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }} fixed>
          <Image
            src={{ uri: Icon }}
            style={{ width: 20 }}
          />
          <Text style={{ fontSize: 14, marginLeft: 5, fontWeight: 'bold', fontFamily: 'Helvetica-Bold' }}>CheckmarkFitness.com</Text>
        </View>
      </Page>
    </Document >
  )
})

Output.propTypes = {
  data: PropTypes.object
}